/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useMemo, useEffect } from 'react';
import { 
  Shield, 
  Settings, 
  ChevronRight, 
  ArrowLeft, 
  Calendar, 
  CheckCircle2, 
  AlertTriangle, 
  XCircle, 
  FileText, 
  Award, 
  Users, 
  Clock, 
  Plus, 
  Search, 
  Filter, 
  Download, 
  Eye, 
  Trash2,
  ExternalLink,
  Flame,
  HardHat,
  HeartPulse,
  Sparkles,
  ShieldCheck,
  Briefcase,
  Layers,
  FileCheck2,
  AlertOctagon,
  MapPin,
  Building2,
  Activity,
  Inbox
} from 'lucide-react';
import { CostCenter, SafetyIncident } from '../types.ts';
import SafetyCrossManager from './SafetyCrossManager.tsx';
import SafetyTalksPlannerManager from './SafetyTalksPlannerManager.tsx';
import DC3Manager from './DC3Manager.tsx';
import HSEQOverviewDashboard from './HSEQOverviewDashboard.tsx';
import { CertificationsManager } from './CertificationsManager.tsx';
import { CompetenciesManager } from './CompetenciesManager.tsx';
import { dispatchCertificatesUpdatedEvent } from '../utils/certificateAlertHelper.ts';
import { 
  getDynamicSecurityData, 
  SafetyTalk, 
  DC3Record, 
  CertificateRecord, 
  CompetencyItem 
} from '../data/securityInitialData.ts';

export type SecuritySection = 
  | 'overview' 
  | 'cruz_seguridad' 
  | 'platicas_5min' 
  | 'formatos_dc3' 
  | 'certificados' 
  | 'matriz_competencias';

interface SecurityInternalManagerProps {
  currentCostCenter?: CostCenter | null;
  costCenters?: CostCenter[];
  onOpenSettings?: () => void;
  onNavigateCostCenter?: (costCenterId: string) => void;
}

export const SecurityInternalManager: React.FC<SecurityInternalManagerProps> = ({
  currentCostCenter,
  costCenters = [],
  onOpenSettings,
  onNavigateCostCenter
}) => {
  const [currentSection, setCurrentSection] = useState<SecuritySection>('overview');
  const [selectedCostCenterId, setSelectedCostCenterId] = useState<string>(
    currentCostCenter?.id || 'CC-MAZDA-BAJIO'
  );

  // Synchronize when currentCostCenter prop changes
  useEffect(() => {
    if (currentCostCenter?.id) {
      setSelectedCostCenterId(currentCostCenter.id);
    }
  }, [currentCostCenter]);

  // Derive active Cost Center
  const activeCostCenter = useMemo(() => {
    if (!costCenters || costCenters.length === 0) return currentCostCenter || null;
    const found = costCenters.find(cc => cc.id === selectedCostCenterId);
    return found || currentCostCenter || costCenters[0] || null;
  }, [costCenters, selectedCostCenterId, currentCostCenter]);

  const storagePrefix = `snf_sec_v3_${activeCostCenter?.id || 'default'}`;

  // State collections with LocalStorage persistence, default clean empty slate
  const [incidents, setIncidents] = useState<SafetyIncident[]>(() => {
    try {
      const saved = localStorage.getItem(`${storagePrefix}_incidents`);
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  const [safetyTalks, setSafetyTalks] = useState<SafetyTalk[]>(() => {
    try {
      const saved = localStorage.getItem(`${storagePrefix}_talks`);
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  const [dc3Records, setDc3Records] = useState<DC3Record[]>(() => {
    try {
      const saved = localStorage.getItem(`${storagePrefix}_dc3`);
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  const [certificates, setCertificates] = useState<CertificateRecord[]>(() => {
    try {
      const saved = localStorage.getItem(`${storagePrefix}_certs`);
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  const [competencies, setCompetencies] = useState<CompetencyItem[]>(() => {
    try {
      const saved = localStorage.getItem(`${storagePrefix}_comp`);
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  // Modals state
  const [isTalkModalOpen, setIsTalkModalOpen] = useState(false);
  const [isDC3ModalOpen, setIsDC3ModalOpen] = useState(false);
  const [isCertModalOpen, setIsCertModalOpen] = useState(false);
  const [isCompModalOpen, setIsCompModalOpen] = useState(false);
  const [searchFilter, setSearchFilter] = useState('');

  // Form states
  const [newTalk, setNewTalk] = useState<Partial<SafetyTalk>>({});
  const [newDC3, setNewDC3] = useState<Partial<DC3Record>>({});
  const [newCert, setNewCert] = useState<Partial<CertificateRecord>>({});
  const [newComp, setNewComp] = useState<{
    employeeName: string;
    role: string;
    area: string;
    skills: { name: string; level: number }[];
  }>({
    employeeName: '',
    role: 'Operador Especializado',
    area: activeCostCenter?.name || 'Planta Operativa',
    skills: [
      { name: 'Manejo de Sustancias Químicas (NOM-018)', level: 3 },
      { name: 'Uso de Equipo de Protección Personal (NOM-017)', level: 4 },
      { name: 'Respuesta ante Emergencias y Evacuación', level: 3 }
    ]
  });

  // Reload when switching active cost center
  useEffect(() => {
    try {
      const inc = localStorage.getItem(`${storagePrefix}_incidents`);
      setIncidents(inc ? JSON.parse(inc) : []);
      const tlk = localStorage.getItem(`${storagePrefix}_talks`);
      setSafetyTalks(tlk ? JSON.parse(tlk) : []);
      const dc = localStorage.getItem(`${storagePrefix}_dc3`);
      setDc3Records(dc ? JSON.parse(dc) : []);
      const crt = localStorage.getItem(`${storagePrefix}_certs`);
      setCertificates(crt ? JSON.parse(crt) : []);
      const cmp = localStorage.getItem(`${storagePrefix}_comp`);
      setCompetencies(cmp ? JSON.parse(cmp) : []);
    } catch (e) {
      console.error('Error loading security data from localStorage', e);
    }
  }, [storagePrefix]);

  // Persist handlers
  const saveIncidents = (updated: SafetyIncident[]) => {
    setIncidents(updated);
    try {
      localStorage.setItem(`${storagePrefix}_incidents`, JSON.stringify(updated));
    } catch (e) {
      console.error('Failed to save incidents to localStorage', e);
    }
  };

  const saveTalks = (updated: SafetyTalk[]) => {
    setSafetyTalks(updated);
    try {
      localStorage.setItem(`${storagePrefix}_talks`, JSON.stringify(updated));
    } catch (e) {
      console.error('Failed to save talks to localStorage', e);
    }
  };

  const saveDC3 = (updated: DC3Record[]) => {
    setDc3Records(updated);
    try {
      localStorage.setItem(`${storagePrefix}_dc3`, JSON.stringify(updated));
    } catch (e) {
      console.error('Failed to save DC3 to localStorage', e);
    }
  };

  const saveCerts = (updated: CertificateRecord[]) => {
    setCertificates(updated);
    try {
      localStorage.setItem(`${storagePrefix}_certs`, JSON.stringify(updated));
      dispatchCertificatesUpdatedEvent();
    } catch (e) {
      console.error('Failed to save certs to localStorage', e);
    }
  };

  const saveCompetencies = (updated: CompetencyItem[]) => {
    setCompetencies(updated);
    try {
      localStorage.setItem(`${storagePrefix}_comp`, JSON.stringify(updated));
    } catch (e) {
      console.error('Failed to save competencies to localStorage', e);
    }
  };

  // Incident Handlers
  const handleSaveIncident = (savedIncident: SafetyIncident) => {
    const idx = incidents.findIndex(i => i.id === savedIncident.id);
    let updated: SafetyIncident[];
    if (idx >= 0) {
      updated = [...incidents];
      updated[idx] = savedIncident;
    } else {
      updated = [savedIncident, ...incidents];
    }
    saveIncidents(updated);
  };

  const handleDeleteIncident = (incidentId: string) => {
    const updated = incidents.filter(i => i.id !== incidentId);
    saveIncidents(updated);
  };

  // Metrics summary
  const totalIncidentsCount = useMemo(() => {
    return incidents.filter(i => i.classification && i.classification !== 'safe').length;
  }, [incidents]);

  const daysWithoutAccident = useMemo(() => {
    const hasLti = incidents.some(i => i.classification === 'lost_time');
    if (hasLti) return 0;
    if (incidents.length === 0) return 0;
    return incidents.filter(i => i.classification === 'safe').length;
  }, [incidents]);

  // Talk handlers
  const handleSaveTalk = (savedTalk: SafetyTalk) => {
    const idx = safetyTalks.findIndex(t => t.id === savedTalk.id);
    let updated: SafetyTalk[];
    if (idx >= 0) {
      updated = [...safetyTalks];
      updated[idx] = savedTalk;
    } else {
      updated = [savedTalk, ...safetyTalks];
    }
    saveTalks(updated);
  };

  const handleBatchSaveTalks = (newTalks: SafetyTalk[], replaceMonth: boolean = false, year?: number, monthIndex?: number) => {
    if (replaceMonth && year !== undefined && monthIndex !== undefined) {
      const monthStr = String(monthIndex + 1).padStart(2, '0');
      const prefix = `${year}-${monthStr}`;
      const remaining = safetyTalks.filter(t => !t.date?.startsWith(prefix));
      saveTalks([...newTalks, ...remaining]);
    } else {
      // Merge by ID or date+topic
      const map = new Map<string, SafetyTalk>();
      safetyTalks.forEach(t => map.set(t.id, t));
      newTalks.forEach(t => map.set(t.id, t));
      saveTalks(Array.from(map.values()));
    }
  };

  const handleAddTalk = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newTalk.topic) return;
    const talk: SafetyTalk = {
      id: `PL-${Date.now().toString().slice(-4)}`,
      topic: newTalk.topic || 'Plática de Seguridad',
      date: newTalk.date || new Date().toISOString().split('T')[0],
      facilitator: newTalk.facilitator || 'Supervisor HSEQ',
      attendeesCount: Number(newTalk.attendeesCount) || 15,
      area: newTalk.area || (activeCostCenter ? activeCostCenter.name : 'Planta'),
      status: (newTalk.status as any) || 'completada',
      description: newTalk.description || 'Instrucción y revisión de medidas preventivas.'
    };
    handleSaveTalk(talk);
    setIsTalkModalOpen(false);
    setNewTalk({});
  };

  const handleDeleteTalk = (id: string) => {
    saveTalks(safetyTalks.filter(t => t.id !== id));
  };

  // DC3 handlers
  const handleAddDC3 = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newDC3.employeeName || !newDC3.courseName) return;
    const dc3: DC3Record = {
      id: `DC3-${Date.now().toString().slice(-4)}`,
      employeeName: newDC3.employeeName || '',
      employeeId: newDC3.employeeId || `ISN-${Math.floor(100 + Math.random() * 900)}`,
      role: newDC3.role || 'Operador',
      courseName: newDC3.courseName || '',
      trainer: newDC3.trainer || 'Instituto de Capacitación STPS',
      stpsReg: newDC3.stpsReg || 'STPS-REG-2026',
      issueDate: newDC3.issueDate || new Date().toISOString().split('T')[0],
      expiryDate: newDC3.expiryDate || '2028-08-20',
      status: 'vigente',
      durationHours: Number(newDC3.durationHours) || 16
    };
    saveDC3([dc3, ...dc3Records]);
    setIsDC3ModalOpen(false);
    setNewDC3({});
  };

  const handleDeleteDC3 = (id: string) => {
    saveDC3(dc3Records.filter(d => d.id !== id));
  };

  // Certificate handlers
  const handleAddCert = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newCert.name) return;
    const cert: CertificateRecord = {
      id: `CERT-${Date.now().toString().slice(-4)}`,
      name: newCert.name || '',
      issuer: newCert.issuer || 'Organismo Certificador',
      code: newCert.code || `CERT-${Date.now().toString().slice(-4)}`,
      category: (newCert.category as any) || 'calidad',
      issueDate: newCert.issueDate || new Date().toISOString().split('T')[0],
      validUntil: newCert.validUntil || '2027-12-31',
      status: 'activo',
      scope: newCert.scope || 'Alcance de certificación operativa.'
    };
    saveCerts([cert, ...certificates]);
    setIsCertModalOpen(false);
    setNewCert({});
  };

  const handleDeleteCert = (id: string) => {
    saveCerts(certificates.filter(c => c.id !== id));
  };

  // Competency handlers
  const handleAddCompetency = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newComp.employeeName) return;
    const avgScore = Math.round(
      (newComp.skills.reduce((acc, s) => acc + s.level, 0) / (newComp.skills.length * 4)) * 100
    );
    const comp: CompetencyItem = {
      id: `COMP-${Date.now().toString().slice(-4)}`,
      employeeName: newComp.employeeName,
      role: newComp.role || 'Operador',
      area: newComp.area || 'Operaciones',
      skills: newComp.skills,
      overallScore: avgScore
    };
    saveCompetencies([comp, ...competencies]);
    setIsCompModalOpen(false);
    setNewComp({
      employeeName: '',
      role: 'Operador Especializado',
      area: activeCostCenter?.name || 'Planta Operativa',
      skills: [
        { name: 'Manejo de Sustancias Químicas (NOM-018)', level: 3 },
        { name: 'Uso de Equipo de Protección Personal (NOM-017)', level: 4 },
        { name: 'Respuesta ante Emergencias y Evacuación', level: 3 }
      ]
    });
  };

  const handleDeleteCompetency = (id: string) => {
    saveCompetencies(competencies.filter(c => c.id !== id));
  };

  const navCostCenterTitle = activeCostCenter ? `${activeCostCenter.name}` : 'Planta Central';

  // Filtered collections
  const filteredTalks = useMemo(() => {
    if (!searchFilter.trim()) return safetyTalks;
    const q = searchFilter.toLowerCase();
    return safetyTalks.filter(t => 
      t.topic.toLowerCase().includes(q) || 
      t.facilitator.toLowerCase().includes(q) || 
      t.area.toLowerCase().includes(q)
    );
  }, [safetyTalks, searchFilter]);

  const filteredDC3 = useMemo(() => {
    if (!searchFilter.trim()) return dc3Records;
    const q = searchFilter.toLowerCase();
    return dc3Records.filter(d => 
      d.employeeName.toLowerCase().includes(q) || 
      d.courseName.toLowerCase().includes(q) || 
      d.trainer.toLowerCase().includes(q)
    );
  }, [dc3Records, searchFilter]);

  const filteredCerts = useMemo(() => {
    if (!searchFilter.trim()) return certificates;
    const q = searchFilter.toLowerCase();
    return certificates.filter(c => 
      c.name.toLowerCase().includes(q) || 
      c.issuer.toLowerCase().includes(q) || 
      c.code.toLowerCase().includes(q)
    );
  }, [certificates, searchFilter]);

  const filteredCompetencies = useMemo(() => {
    if (!searchFilter.trim()) return competencies;
    const q = searchFilter.toLowerCase();
    return competencies.filter(c => 
      c.employeeName.toLowerCase().includes(q) || 
      c.role.toLowerCase().includes(q) || 
      c.area.toLowerCase().includes(q)
    );
  }, [competencies, searchFilter]);

  return (
    <div className="space-y-6 animate-fadeIn pb-12">
      
      {/* Top Header Card */}
      <div className="bg-[#181920] border border-[#2b2d38] rounded-2xl p-5 sm:p-6 shadow-xl relative overflow-hidden">
        <div className="absolute -right-16 -top-16 w-56 h-56 bg-[#e52e2e]/5 rounded-full blur-3xl pointer-events-none"></div>

        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-5 relative z-10">
          <div className="space-y-1.5">
            <div className="flex items-center gap-2.5 flex-wrap">
              <div className="p-2 rounded-xl bg-[#e52e2e]/10 text-[#e52e2e] border border-[#e52e2e]/20">
                <ShieldCheck className="h-6 w-6" />
              </div>
              <h1 className="text-xl sm:text-2xl font-black text-white tracking-tight">
                Gestión de Seguridad Industrial y Salud Ocupacional (HSEQ)
              </h1>
              <span className="bg-emerald-950/80 text-emerald-300 border border-emerald-700/70 text-xs px-2.5 py-0.5 rounded-full font-mono font-bold flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse"></span>
                NOM-019-STPS / ISO 45001
              </span>
            </div>
            <p className="text-xs sm:text-sm text-slate-400 max-w-3xl">
              Sistema integral de control de riesgos, cruz industrial, pláticas de 5 minutos, constancias DC-3, certificados y matriz de competencias por sede.
            </p>
          </div>
        </div>

        {/* Section Navigation Tabs */}
        <div className="flex flex-wrap items-center gap-2 mt-6 pt-5 border-t border-[#262734]">
          {[
            { id: 'overview', label: 'Resumen HSEQ', icon: Activity },
            { id: 'cruz_seguridad', label: 'Cruz de Seguridad', icon: ShieldCheck, badge: `${totalIncidentsCount} eventos` },
            { id: 'platicas_5min', label: 'Pláticas de 5 Min', icon: Users, badge: `${safetyTalks.length}` },
            { id: 'formatos_dc3', label: 'Formatos DC-3 (STPS)', icon: FileCheck2, badge: `${dc3Records.length}` },
            { id: 'certificados', label: 'Certificaciones y Auditorías', icon: Award, badge: `${certificates.length}` },
            { id: 'matriz_competencias', label: 'Matriz de Competencias', icon: Layers, badge: `${competencies.length}` }
          ].map(tab => {
            const Icon = tab.icon;
            const isActive = currentSection === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => {
                  setCurrentSection(tab.id as SecuritySection);
                  setSearchFilter('');
                }}
                className={`flex items-center gap-2 px-3.5 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                  isActive
                    ? 'bg-[#e52e2e] text-white shadow-lg shadow-[#e52e2e]/20 ring-1 ring-[#ff4a4a]'
                    : 'bg-[#131418] text-slate-400 hover:text-white hover:bg-[#20222a] border border-[#242630]'
                }`}
              >
                <Icon className="h-4 w-4" />
                <span>{tab.label}</span>
                {tab.badge && (
                  <span className={`text-[10px] px-1.5 py-0.2 rounded-md font-mono ${
                    isActive ? 'bg-black/30 text-white' : 'bg-[#22242e] text-slate-400'
                  }`}>
                    {tab.badge}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Main Content Area */}
      {currentSection === 'cruz_seguridad' && (
        <SafetyCrossManager
          activeCostCenter={activeCostCenter}
          incidents={incidents}
          onSaveIncident={handleSaveIncident}
          onDeleteIncident={handleDeleteIncident}
        />
      )}

      {currentSection === 'overview' && (
        <HSEQOverviewDashboard
          activeCostCenter={activeCostCenter}
          daysWithoutIncident={daysWithoutAccident}
          recordDays={0}
          incidents={incidents}
          safetyTalks={safetyTalks}
          dc3Records={dc3Records}
          certificates={certificates}
          competencies={competencies}
          onNavigateSection={(section) => setCurrentSection(section)}
        />
      )}

      {/* Pláticas de 5 min View */}
      {currentSection === 'platicas_5min' && (
        <SafetyTalksPlannerManager
          activeCostCenter={activeCostCenter}
          safetyTalks={safetyTalks}
          onSaveTalk={handleSaveTalk}
          onBatchSaveTalks={handleBatchSaveTalks}
          onDeleteTalk={handleDeleteTalk}
        />
      )}

      {/* Formatos DC3 View */}
      {currentSection === 'formatos_dc3' && (
        <DC3Manager
          activeCostCenter={activeCostCenter}
          dc3Records={dc3Records}
          onSaveDC3={saveDC3}
          onDeleteDC3={handleDeleteDC3}
        />
      )}

      {/* Certificaciones View */}
      {currentSection === 'certificados' && (
        <CertificationsManager
          activeCostCenter={activeCostCenter}
          costCenters={costCenters}
          certificates={certificates}
          onSaveCertificates={saveCerts}
        />
      )}

      {/* Matriz de Competencias I.L.U. View */}
      {currentSection === 'matriz_competencias' && (
        <CompetenciesManager
          activeCostCenter={activeCostCenter}
          costCenters={costCenters}
        />
      )}

      {/* Modal Nueva Plática */}
      {isTalkModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
          <div className="bg-[#16171c] border border-[#2f313e] rounded-2xl max-w-lg w-full p-6 space-y-4 text-white">
            <h3 className="font-bold text-base">Registrar Plática de 5 Minutos</h3>
            <form onSubmit={handleAddTalk} className="space-y-3 text-xs">
              <div>
                <label className="block text-slate-400 mb-1">Tema de la Plática *</label>
                <input
                  type="text"
                  required
                  placeholder="Ej: Uso de lavaojos y regaderas de emergencia..."
                  value={newTalk.topic || ''}
                  onChange={(e) => setNewTalk({ ...newTalk, topic: e.target.value })}
                  className="w-full bg-[#101114] border border-[#2a2c38] rounded-lg p-2 text-white"
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-slate-400 mb-1">Fecha</label>
                  <input
                    type="date"
                    value={newTalk.date || new Date().toISOString().split('T')[0]}
                    onChange={(e) => setNewTalk({ ...newTalk, date: e.target.value })}
                    className="w-full bg-[#101114] border border-[#2a2c38] rounded-lg p-2 text-white"
                  />
                </div>
                <div>
                  <label className="block text-slate-400 mb-1">No. Asistentes</label>
                  <input
                    type="number"
                    value={newTalk.attendeesCount || 15}
                    onChange={(e) => setNewTalk({ ...newTalk, attendeesCount: parseInt(e.target.value) || 0 })}
                    className="w-full bg-[#101114] border border-[#2a2c38] rounded-lg p-2 text-white"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-slate-400 mb-1">Facilitador / Supervisor</label>
                  <input
                    type="text"
                    placeholder="Nombre del facilitador..."
                    value={newTalk.facilitator || ''}
                    onChange={(e) => setNewTalk({ ...newTalk, facilitator: e.target.value })}
                    className="w-full bg-[#101114] border border-[#2a2c38] rounded-lg p-2 text-white"
                  />
                </div>
                <div>
                  <label className="block text-slate-400 mb-1">Área Operativa</label>
                  <input
                    type="text"
                    placeholder="Ej: Decapado Químico"
                    value={newTalk.area || ''}
                    onChange={(e) => setNewTalk({ ...newTalk, area: e.target.value })}
                    className="w-full bg-[#101114] border border-[#2a2c38] rounded-lg p-2 text-white"
                  />
                </div>
              </div>
              <div>
                <label className="block text-slate-400 mb-1">Descripción / Puntos Clave</label>
                <textarea
                  rows={3}
                  value={newTalk.description || ''}
                  onChange={(e) => setNewTalk({ ...newTalk, description: e.target.value })}
                  className="w-full bg-[#101114] border border-[#2a2c38] rounded-lg p-2 text-white"
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setIsTalkModalOpen(false)}
                  className="px-4 py-2 rounded-xl bg-[#20222a] text-slate-300 cursor-pointer"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 rounded-xl bg-[#e52e2e] text-white font-bold cursor-pointer"
                >
                  Guardar Plática
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Nuevo Certificado */}
      {isCertModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
          <div className="bg-[#16171c] border border-[#2f313e] rounded-2xl max-w-lg w-full p-6 space-y-4 text-white">
            <h3 className="font-bold text-base">Registrar Certificado o Auditoría</h3>
            <form onSubmit={handleAddCert} className="space-y-3 text-xs">
              <div>
                <label className="block text-slate-400 mb-1">Nombre de la Certificación *</label>
                <input
                  type="text"
                  required
                  placeholder="Ej: ISO 45001:2018 Seguridad y Salud en el Trabajo..."
                  value={newCert.name || ''}
                  onChange={(e) => setNewCert({ ...newCert, name: e.target.value })}
                  className="w-full bg-[#101114] border border-[#2a2c38] rounded-lg p-2 text-white"
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-slate-400 mb-1">Organismo Emisor</label>
                  <input
                    type="text"
                    value={newCert.issuer || ''}
                    onChange={(e) => setNewCert({ ...newCert, issuer: e.target.value })}
                    className="w-full bg-[#101114] border border-[#2a2c38] rounded-lg p-2 text-white"
                  />
                </div>
                <div>
                  <label className="block text-slate-400 mb-1">Código / Folio</label>
                  <input
                    type="text"
                    value={newCert.code || ''}
                    onChange={(e) => setNewCert({ ...newCert, code: e.target.value })}
                    className="w-full bg-[#101114] border border-[#2a2c38] rounded-lg p-2 text-white"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-slate-400 mb-1">Categoría</label>
                  <select
                    value={newCert.category || 'seguridad'}
                    onChange={(e) => setNewCert({ ...newCert, category: e.target.value as any })}
                    className="w-full bg-[#101114] border border-[#2a2c38] rounded-lg p-2 text-white"
                  >
                    <option value="seguridad">Seguridad y Salud (ISO 45001)</option>
                    <option value="calidad">Calidad (ISO 9001 / IATF)</option>
                    <option value="ambiental">Gestión Ambiental (ISO 14001)</option>
                    <option value="proteccion_civil">Protección Civil</option>
                    <option value="metrologia">Metrología</option>
                  </select>
                </div>
                <div>
                  <label className="block text-slate-400 mb-1">Vigencia Hasta</label>
                  <input
                    type="date"
                    value={newCert.validUntil || '2028-12-31'}
                    onChange={(e) => setNewCert({ ...newCert, validUntil: e.target.value })}
                    className="w-full bg-[#101114] border border-[#2a2c38] rounded-lg p-2 text-white"
                  />
                </div>
              </div>
              <div>
                <label className="block text-slate-400 mb-1">Alcance Certificado</label>
                <textarea
                  rows={2}
                  value={newCert.scope || ''}
                  onChange={(e) => setNewCert({ ...newCert, scope: e.target.value })}
                  className="w-full bg-[#101114] border border-[#2a2c38] rounded-lg p-2 text-white"
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setIsCertModalOpen(false)}
                  className="px-4 py-2 rounded-xl bg-[#20222a] text-slate-300 cursor-pointer"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 rounded-xl bg-[#e52e2e] text-white font-bold cursor-pointer"
                >
                  Guardar Certificado
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Nueva Competencia */}
      {isCompModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
          <div className="bg-[#16171c] border border-[#2f313e] rounded-2xl max-w-lg w-full p-6 space-y-4 text-white max-h-[90vh] overflow-y-auto">
            <h3 className="font-bold text-base">Evaluar Colaborador en Habilidades HSEQ</h3>
            <form onSubmit={handleAddCompetency} className="space-y-3 text-xs">
              <div>
                <label className="block text-slate-400 mb-1">Nombre del Colaborador *</label>
                <input
                  type="text"
                  required
                  placeholder="Nombre y apellido..."
                  value={newComp.employeeName}
                  onChange={(e) => setNewComp({ ...newComp, employeeName: e.target.value })}
                  className="w-full bg-[#101114] border border-[#2a2c38] rounded-lg p-2 text-white"
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-slate-400 mb-1">Puesto / Función</label>
                  <input
                    type="text"
                    value={newComp.role}
                    onChange={(e) => setNewComp({ ...newComp, role: e.target.value })}
                    className="w-full bg-[#101114] border border-[#2a2c38] rounded-lg p-2 text-white"
                  />
                </div>
                <div>
                  <label className="block text-slate-400 mb-1">Área</label>
                  <input
                    type="text"
                    value={newComp.area}
                    onChange={(e) => setNewComp({ ...newComp, area: e.target.value })}
                    className="w-full bg-[#101114] border border-[#2a2c38] rounded-lg p-2 text-white"
                  />
                </div>
              </div>

              <div className="pt-2 border-t border-[#252733] space-y-2">
                <label className="block text-slate-300 font-bold">Evaluación por Competencia (Nivel 1 al 4):</label>
                {newComp.skills.map((s, idx) => (
                  <div key={idx} className="p-2.5 rounded-lg bg-[#111216] border border-[#262835] space-y-1.5">
                    <div className="flex justify-between items-center">
                      <span className="text-white font-medium">{s.name}</span>
                      <span className="font-mono text-amber-400 font-bold">Nivel {s.level}</span>
                    </div>
                    <input
                      type="range"
                      min={1}
                      max={4}
                      step={1}
                      value={s.level}
                      onChange={(e) => {
                        const next = [...newComp.skills];
                        next[idx].level = parseInt(e.target.value);
                        setNewComp({ ...newComp, skills: next });
                      }}
                      className="w-full accent-emerald-500 cursor-pointer"
                    />
                  </div>
                ))}
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setIsCompModalOpen(false)}
                  className="px-4 py-2 rounded-xl bg-[#20222a] text-slate-300 cursor-pointer"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 rounded-xl bg-[#e52e2e] text-white font-bold cursor-pointer"
                >
                  Guardar Evaluación
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
};

export default SecurityInternalManager;

